import { ALPHABET } from './alphabet'
import { fakeCryptoRandom } from './fake-random'
import { sampleAlphabetChars } from './sample'
import { cryptoRandom, type Random } from './types'

export const ulid = (seedTime?: number) => {
  const now = seedTime ?? Date.now()
  return encodeTime(now) + encodeRandom(cryptoRandom)
}
export const getCreatedAt = (ulid: string) => new Date(decodeTime(ulid)) // string & { getCreatedAt: () => Date } in prototype breaks ts
export const msFromUlid = (ulid: string) => decodeTime(ulid)

export type Ulid = string

const ENCODING = `-${ALPHABET}`
const CHAR_MAP = new Map(ENCODING.split('').map((char, index) => [char, index]))
const ALLOWED_LEN = ALPHABET.length

const TIME_LEN = 7
const RANDOM_LEN = 12 // 8B per ms to get 1% collision rate

function encodeTime(now = Date.now()) {
  let mod: number
  let str = ''
  for (let currentLen = TIME_LEN; currentLen > 0; currentLen--) {
    mod = now % ALLOWED_LEN
    str = ALPHABET.charAt(mod) + str
    now = (now - mod) / ALLOWED_LEN
  }
  return str
}

function encodeRandom(random: Random) {
  // Rejection sampling (not modulo/bitmask) because ALLOWED_LEN (63) is not a
  // power of 2 and doesn't evenly divide 256; see sample.ts for why.
  return sampleAlphabetChars(fakeCryptoRandom ?? random, RANDOM_LEN)
}

function _makeUlid(random: Random) {
  return () => encodeTime() + encodeRandom(random)
}

export function monotonicFactory() {
  // -1 (never a real timestamp) so the very first call always falls into the
  // "new timestamp" branch below, even when called with seedTime 0.
  let lastTime = -1
  let lastRandom = ''

  return (seedTime?: number) => {
    const now = seedTime ?? Date.now()

    if (now > lastTime) {
      // New (strictly increasing) timestamp - generate new random part
      lastTime = now
      lastRandom = encodeRandom(cryptoRandom)
    } else {
      // Same or decreasing timestamp - increment the random part instead of
      // lowering the time prefix, so the result still sorts after the last
      // ulid; keep encoding lastTime, not the (possibly smaller) `now`.
      lastRandom = incrementBase64(lastRandom)
    }

    return encodeTime(lastTime) + lastRandom
  }
}

function incrementBase64(str: string): string {
  const chars = str.split('')

  for (let i = chars.length - 1; i >= 0; i--) {
    const currentIndex = ALPHABET.indexOf(chars[i])

    if (currentIndex === -1) {
      throw new Error(`Invalid character '${chars[i]}' in random part`)
    }

    if (currentIndex < ALLOWED_LEN - 1) {
      // Can increment this character
      chars[i] = ALPHABET[currentIndex + 1]
      return chars.join('')
    }
    // Overflow - set to 0 and carry to next position
    chars[i] = ALPHABET[0]
  }

  // All characters overflowed - wrap around
  return chars.join('')
}

export function decodeTime(ulid: string) {
  const timePart = ulid.slice(0, TIME_LEN)
  let timestamp = 0

  for (let i = 0; i < TIME_LEN; i++) {
    const char = timePart.charAt(i)
    const index = ALPHABET.indexOf(char)
    if (index === -1)
      throw new Error(`Invalid character '${char}' found in ULID.`)
    timestamp = timestamp * ALLOWED_LEN + index
  }

  return timestamp
}

export function ulidToBytes(ulid: string) {
  const bitArray = []
  for (let i = 0; i < ulid.length; i++) {
    const charIndex = CHAR_MAP.get(ulid[i])
    if (charIndex === undefined)
      throw new Error(`Character "${ulid[i]}" is not in the charset`)
    for (let j = 5; j >= 0; j--) bitArray.push((charIndex >> j) & 1)
  }

  // Calculate the length of the byte array
  const byteArrayLength = Math.ceil(bitArray.length / 8)
  const byteArray = new Uint8Array(byteArrayLength)

  // Convert bitArray to Uint8Array
  for (let i = 0; i < bitArray.length; i++)
    byteArray[Math.floor(i / 8)] |= bitArray[i] << (7 - (i % 8))

  // Store the length of the original string as the last byte
  const resultArray = new Uint8Array(byteArrayLength + 1)
  resultArray.set(byteArray)
  resultArray[byteArrayLength] = ulid.length

  return resultArray
}

export function bytesToUlid(array: Uint8Array) {
  const originalLength = array.at(-1) ?? 0
  const byteArray = array.slice(0, array.length - 1)

  const bitArray = []
  for (let i = 0; i < byteArray.length; i++) {
    for (let j = 7; j >= 0; j--) bitArray.push((byteArray[i] >> j) & 1)
  }

  let str = ''
  for (let i = 0; i < originalLength * 6; i += 6) {
    let charIndex = 0
    for (let j = 0; j < 6; j++)
      charIndex = (charIndex << 1) | (bitArray[i + j] || 0)
    str += ENCODING[charIndex]
  }
  return str
}

// oxlint-disable-next-line typescript/no-explicit-any -- idk
export function _bytesToUlid(input: any) {
  if (isString(input)) input = hexToUlidBytes(input)
  // if (!('byteLength' in input)) throw new Error(`Expected input to have bufferLength property, got: ${input}`) // doesn't work for d1
  // oxlint-disable-next-line typescript/no-unsafe-member-access -- input is any by design; runtime guard
  if (typeof input[0] !== 'number')
    // !Array.isArray(input) fails for Uint8Array
    throw new Error(`Expected input to be an array of numbers, got: ${input}`)
  // oxlint-disable-next-line typescript/no-explicit-any -- idk
  return bytesToUlid(input as Uint8Array)
}

export const ulidBytesToHex = (ulid: Uint8Array) =>
  Array.prototype.map
    .call(ulid, (x: number) => `00${x.toString(16)}`.slice(-2))
    .join('')

export const hexToUlidBytes = (hex: string) => {
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = Number.parseInt(hex.slice(i, i + 2), 16)
  }
  return bytes
}

export function getUlidBefore(ulid: string): string {
  const currentTimestamp = decodeTime(ulid)
  if (currentTimestamp === 0)
    throw new Error('Cannot decrement ULID timestamp below 0.')

  const newTimestamp = currentTimestamp - 1
  const newTimePart = encodeTime(newTimestamp)

  const randomPart = ulid.slice(TIME_LEN) // preserve random part of existing ULID

  return newTimePart + randomPart
}

const isString = (input: unknown): input is string => typeof input === 'string'
